import { Pencil, Plus, TriangleAlert, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { needsApi } from '../api/endpoints'
import { NEED_CATEGORY_LABELS, summarizeJson } from '../utils/taxonomy'
import { NeedForm } from './NeedForm'
import type { LearnerNeedResponse } from '../types/api'

/** Same list, in a guardian's own words - it is still their account's need row underneath. */
const COPY = {
  learner: {
    heading: 'What I want to learn',
    blurb: 'Add everything you want to learn - they are matched separately.',
    empty: 'You have not added what you want to learn yet. Add a category and subject below so the map can match you with the right tutors.',
  },
  guardian: {
    heading: "What my child wants to learn",
    blurb: "Add everything your child is looking for - they are matched separately.",
    empty: 'You have not added what your child wants to learn yet. Add a category and subject below so the map can match you with the right tutors.',
  },
}

export function NeedsSection({
  needs,
  onChanged,
  forGuardian = false,
}: {
  needs: LearnerNeedResponse[]
  onChanged: () => void
  forGuardian?: boolean
}) {
  const copy = forGuardian ? COPY.guardian : COPY.learner
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  async function handleDelete(id: string) {
    if (!confirm('Remove this need?')) return
    await needsApi.remove(id)
    onChanged()
  }

  async function handleMakePrimary(need: LearnerNeedResponse) {
    await needsApi.update(need.id, {
      category: need.category,
      items: need.items,
      detail: need.detail,
      level: need.level,
      mode: need.mode,
      timing: need.timing,
      budgetBand: need.budgetBand,
      preferredTutorGender: need.preferredTutorGender,
      isPrimary: true,
    })
    onChanged()
  }

  return (
    <div id="needs">
      <h2 className="text-lg font-bold text-ink-900">{copy.heading}</h2>
      <p className="mb-3 text-sm text-ink-500">{copy.blurb}</p>

      {needs.length === 0 && (
        <p className="mb-3 flex items-start gap-2 rounded-xl border border-sun-400/50 bg-sun-400/10 px-3 py-2 text-sm text-amber-900">
          <TriangleAlert size={15} className="mt-0.5 shrink-0" />
          {copy.empty}
        </p>
      )}

      <div className="space-y-3">
        {needs.map((need) =>
          editingId === need.id ? (
            <NeedForm
              key={need.id}
              initial={need}
              onCancel={() => setEditingId(null)}
              onSaved={() => {
                setEditingId(null)
                onChanged()
              }}
            />
          ) : (
            <div key={need.id} className="card-pad flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {/* The active chip style, not plain text: this is the category the map actually
                      matches you against (see DiscoveryService.matchesCategoryAndSubject), so it
                      reads as a live selection rather than a label. */}
                  <span className="chip chip-active !cursor-default !py-1 text-sm font-bold">
                    {NEED_CATEGORY_LABELS[need.category]}
                  </span>
                  {need.isPrimary && <span className="pill bg-sun-400/20 text-amber-800">Primary</span>}
                </div>
                <p className="text-sm text-ink-500">
                  {[summarizeJson(need.items), need.level, need.mode, summarizeJson(need.timing), need.budgetBand]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {!need.isPrimary && (
                  <button onClick={() => handleMakePrimary(need)} className="btn-ghost !px-3 !py-1.5 text-xs">
                    Make primary
                  </button>
                )}
                <button onClick={() => setEditingId(need.id)} className="grid h-8 w-8 place-items-center rounded-full border border-ink-200 hover:bg-ink-100">
                  <Pencil size={14} />
                </button>
                <button onClick={() => handleDelete(need.id)} className="grid h-8 w-8 place-items-center rounded-full border border-ink-200 hover:bg-ink-100">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ),
        )}
      </div>

      {adding ? (
        <div className="mt-3">
          <NeedForm
            onCancel={() => setAdding(false)}
            onSaved={() => {
              setAdding(false)
              onChanged()
            }}
          />
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="btn-ghost mt-3 gap-1.5">
          <Plus size={15} /> Add a need
        </button>
      )}
    </div>
  )
}
